import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { applyTabLevels, hoistProjectTabs, RouteLike, TabLevel, TabParentFactory } from './hoistProjectTabs'
import ContentLoader from '../components/UI/ContentLoader'
import toast from 'react-hot-toast'
import { isAuthenticated } from '../hooks/CookiesHook'
import { getFirstAllowedNaiFlowDashboardPath, getFirstAllowedNaiHubDashboardPath, getLocalStorage, hasPermission, homeScreenRedirection, naiFlowAuthTab } from '../utils/helper'

// Lazy load pages
const PageNotFound = lazy(() => import('../pages/PageNotFound'))
const Login = lazy(() => import('../pages/Login'))
const OTPVerification = lazy(() => import('../pages/OTPVerification'))
const SelectWorkspace = lazy(() => import('../pages/SelectWorkspace'))
// Dashboard
const Dashboard = lazy(() => import('../pages/Dashboard'))
const ObservationsListing = lazy(() => import('../pages/Dashboard/Listings/Observations'))
const IncidentsListing = lazy(() => import('../pages/Dashboard/Listings/Incidents'))
const InspectionsListing = lazy(() => import('../pages/Dashboard/Listings/Inspections'))
const TrainingsListing = lazy(() => import('../pages/Dashboard/Listings/Trainings'))
const StatisticsCaptureDashboard = lazy(() => import('../pages/Dashboard/StatisticsCaptureDashboard'))
const IncidentDashboard = lazy(() => import('../pages/Dashboard/IncidentDashboard'))
const InspectionDashboard = lazy(() => import('../pages/Dashboard/InspectionDashboard'))
const TrainingDashboard = lazy(() => import('../pages/Dashboard/TrainingDashboard'))
const TrainingDashboardNew = lazy(() => import('../pages/Dashboard/TrainingDashboardNew'))
const OverviewDashboard = lazy(() => import('../pages/Dashboard/OverviewDashboard'))
const OverviewDashboardNew = lazy(() => import('../pages/Dashboard/OverviewDashboardNew'))
const ObservationDashboardNew = lazy(() => import('../pages/Dashboard/ObservationDashboardNew'))
const IncidentDashboardNew = lazy(() => import('../pages/Dashboard/IncidentDashboardNew'))
const AIInsights = lazy(() => import('../pages/Dashboard/AIInsights'))

/** Redirects /dashboard/nai-flow to the first nAI Flow tab the user has permission for. */
const NaiFlowDashboardRedirect = () => <Navigate to={getFirstAllowedNaiFlowDashboardPath()} replace />

/** Same for /dashboard/nai-hub. Hub tabs come from the permission list, so a user with none
 *  still gets the Coming Soon placeholder rather than a dead redirect. */
const NaiHubDashboardRedirect = () => {
  const path = getFirstAllowedNaiHubDashboardPath()
  // Toast in an effect, not during render — react-hot-toast updates the Toaster's state.
  useEffect(() => {
    if (!path) toast.error('You do not have access to nAI Hub.')
  }, [path])
  return <Navigate to={path || homeScreenRedirection()} replace />
}

/** If user has no Observation tab permission, redirect to first allowed nAI Flow tab instead of rendering ObservationDashboardNew. */
const ObservationRouteGuard = () => {
  const permissions = JSON.parse(getLocalStorage('permissions') || '[]')
  if (!hasPermission(permissions, 'observations-dashboard', 1)) {
    return <Navigate to={getFirstAllowedNaiFlowDashboardPath()} replace />
  }
  const breadcrumbData = [
    { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
    { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
    { title: 'Observation', redirect: '/dashboard/nai-flow/observation' },
  ]
  return (
    // Row 2 comes from the dashboard's hoisted strip (DASHBOARD_LEVELS); this only sets breadcrumbs.
    <PageBreadcrumb breadcrumbData={breadcrumbData}>
      <ObservationDashboardNew />
    </PageBreadcrumb>
  )
}

// Nai Hub Dashboard
const NaiHubDashboard = lazy(() => import('../pages/Dashboard/NaiHubDashboard'))
// User Management
const UserRole = lazy(() => import('../pages/UserManagement/UserRole'))
const UserListing = lazy(() => import('../pages/UserManagement/UserListing'))
const AddUserRole = lazy(() => import('../pages/UserManagement/AddUserRole'))
const AddUser = lazy(() => import('../pages/UserManagement/AddUser'))
// nAI Connect
const NaiConnect = lazy(() => import('../pages/NaiConnect'))
// Quiz Assessment
const QuizAssessment = lazy(() => import('../pages/QuizAssessment'))
const QuizAssessmentDetails = lazy(() => import('../pages/QuizAssessment/QuizAssessmentDetails'))
// Video Script Generation
const VideoScriptGeneration = lazy(() => import('../pages/VideoScriptGeneration'))
// Project Management Start
const ProjectManagement = lazy(() => import('../pages/ProjectManagement'))
const AddProject = lazy(() => import('../pages/ProjectManagement/AddProject'))
// Project's Rules And Escalation
const RulesAndEscalation = lazy(() => import('../pages/ProjectManagement/RulesAndEscalation'))
const AddRulesAndEscalation = lazy(() => import('../pages/ProjectManagement/RulesAndEscalation/AddRulesAndEscalation'))
// Project's User Management
const ProjectUsers = lazy(() => import('../pages/ProjectManagement/ProjectUsers'))
const AddUserToProject = lazy(() => import('../pages/ProjectManagement/ProjectUsers/AddUserToProject'))
const ProjectRoles = lazy(() => import('../pages/ProjectManagement/ProjectRoles'))
const AddRoleToProject = lazy(() => import('../pages/ProjectManagement/ProjectRoles/AddRoleToProject'))
// nAI Flow
const WhatsappGroup = lazy(() => import('../pages/ProjectManagement/NaiFlow/WhatsappGroup'))
const Observations = lazy(() => import('../pages/ProjectManagement/NaiFlow/Observations'))
const Incidents = lazy(() => import('../pages/ProjectManagement/NaiFlow/Incidents'))
const SiteWalkthrough = lazy(() => import('../pages/ProjectManagement/NaiFlow/SiteWalkthrough'))
const SiteWalkthroughDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/SiteWalkthrough/SiteWalkthroughDetails'))
const SiteWalkthroughView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/SiteWalkthrough'))
const SiteWalkthroughDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/SiteWalkthrough/SiteWalkthroughDetailsView'))
const ObservationDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Observations/ObservationDetails'))
const IncidentDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Incidents/IncidentDetails'))
const Inspections = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections'))
const NonConformance = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/NonConformance'))
const NonConformanceDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/NonConformance/NonConformanceDetails'))
const InspectionDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/InspectionDetails'))
const Schedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/Schedule'))
const AddInspectionSchedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/Schedule/AddInspectionSchedule'))
const Trainings = lazy(() => import('../pages/ProjectManagement/NaiFlow/Trainings'))
const TrainingDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Trainings/TrainingDetails'))
// statistics Capture
const StatisticsCapture = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/Schedule'))
const AddSchedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/Schedule/AddSchedule'))
const StatisticsCaptureReport = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/StatisticsCaptureReport'))
const EditReport = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/StatisticsCaptureReport/EditReport'))

// nAI Flow VIew
const WhatsappGroupView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/WhatsappGroup'))
const ObservationsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Observations'))
const IncidentsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Incidents'))
const ObservationDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Observations/ObservationDetails'))
const IncidentDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Incidents/IncidentDetails'))
const InspectionsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections'))
const NonConformanceView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/NonConformance'))
const NonConformanceDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/NonConformance/NonConformanceDetails'))
const InspectionDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/InspectionDetails'))
const ScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/Schedule'))
const AddInspectionScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/Schedule/AddInspectionSchedule'))
const TrainingsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Trainings'))
const TrainingDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Trainings/TrainingDetails'))
// statistics Capture
const StatisticsCaptureView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/Schedule'))
const AddScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/Schedule/AddSchedule'))
const StatisticsCaptureReportView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/StatisticsCaptureReport'))
const EditReportView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/StatisticsCaptureReport/EditReport'))

// nAI Hub
const NaiHub = lazy(() => import('../pages/ProjectManagement/NaiHub'))
const AddNaiHub = lazy(() => import('../pages/ProjectManagement/NaiHub/AddNaiHub'))
// const NaiHubDashboard = lazy(() => import('../pages/ProjectManagement/NaiHub/NaiHubDashboard'))

const ProjectWorkflow = lazy(() => import('../pages/ProjectManagement/ProjectWorkflow'))
// Project Management End

// Workflow Builder
const WorkflowBuilder = lazy(() => import('../pages/WorkflowBuilder'))
const EditWorkflow = lazy(() => import('../pages/WorkflowBuilder/EditWorkflow'))

// AI RAMS Assessment
const AiRamsAssessment = lazy(() => import('../pages/AiRamsAssessment'))

// Masters pages
const Division = lazy(() => import('../pages/Masters/Division'))
const MastersHome = lazy(() => import('../pages/Masters/MastersHome'))
const HazardType = lazy(() => import('../pages/Masters/HazardType'))
const RootCause = lazy(() => import('../pages/Masters/RootCause'))
const Severity = lazy(() => import('../pages/Masters/Severity'))
const ObservationStatus = lazy(() => import('../pages/Masters/ObservationStatus'))
const StatisticsCaptureStatus = lazy(() => import('../pages/Masters/StatisticsCaptureStatus'))
const StatisticsCaptureInputs = lazy(() => import('../pages/Masters/StatisticsCaptureInputs'))
const IncidentStatus = lazy(() => import('../pages/Masters/IncidentStatus'))
const IncidentCategory = lazy(() => import('../pages/Masters/IncidentCategory'))
const IncidentSeverity = lazy(() => import('../pages/Masters/IncidentSeverity'))
const InspectionStatus = lazy(() => import('../pages/Masters/InspectionStatus'))
const InspectionScheduleStatus = lazy(() => import('../pages/Masters/InspectionScheduleStatus'))
const InspectionNonConformanceStatus = lazy(() => import('../pages/Masters/InspectionNonConformanceStatus'))
const InspectionSubCategory = lazy(() => import('../pages/Masters/InspectionSubCategory'))
const InspectionTopic = lazy(() => import('../pages/Masters/InspectionTopic'))
const InspectionQuestions = lazy(() => import('../pages/Masters/InspectionQuestions'))
const TrainingCategory = lazy(() => import('../pages/Masters/TrainingCategory'))

// Lazy load components
const PageBreadcrumb = lazy(() => import('../components/UI/PageBreadcrumb'))
const LRFLayout = lazy(() => import('../components/UI/Layouts/LRFLayout'))
const ProjectLayout = lazy(() => import('../components/UI/Layouts/ProjectLayout'))
const TabsLayout = lazy(() => import('../components/UI/Layouts/TabsLayout'))
const UserManagementLayout = lazy(() => import('../components/UI/Layouts/UserManagementLayout'))
const ComingSoon = lazy(() => import('../components/ComingSoon'))
const CheckAppInstalled = lazy(() => import('../pages/CheckAppInstalled'))
const SSO = lazy(() => import('../pages/SSO'))
const AppLayout = lazy(() => import('../components/UI/Layouts/AppLayout'))

// if already authenticated and try to access login page, redirect to dashboard
const PublicRoute = ({ children }: any) => {
  const permission_details = getLocalStorage('permissions') ? JSON.parse(`${getLocalStorage('permissions')}`) : []

  let redirectPath = homeScreenRedirection()
  const paths = [
    { path: naiFlowAuthTab, slug: 'dashboard' },
    { path: '/reports', slug: 'reports' },
    { path: '/user-management/user-role', slug: 'user-management' },
    { path: '/user-management/user-role', slug: 'user' },
    { path: '/project-management', slug: 'project-management' },
    { path: '/project-management', slug: 'project-list' },
  ] as any
  if (!permission_details?.find((p: any) => p?.slug === 'observations')) {
    const firstSlug = permission_details[0]?.slug
    if (paths?.find((p: any) => p?.slug === firstSlug) && firstSlug) {
      redirectPath = paths?.find((p: any) => p?.slug === firstSlug)?.path
    }
  }

  if (isAuthenticated()) {
    // Hard redirect (not <Navigate>) so this passes back through SubsidiaryBoundary and
    // picks up the subsidiary-ID prefix — an in-SPA <Navigate> here would land inside the
    // basename:'' router this component itself renders under, producing an unprefixed URL.
    window.location.replace(redirectPath)
    return null
  }
  return children
}

// if not authenticated and try to access protected page, redirect to login.
// `permission` is opt-in per route (most private routes don't set it, and
// keep relying on the sidebar to hide links + server-side authorization on
// every API call, same as before) — when a route does set it, an
// authenticated user who lacks that module/permission is redirected away
// rather than shown a page with nothing it can load.
const ProtectedRoute = ({ children, permission }: any) => {
  if (!isAuthenticated()) return <Navigate to="/login" />
  if (permission) {
    const permissions = JSON.parse(getLocalStorage('permissions') || '[]')
    if (!hasPermission(permissions, permission.slug, permission.permissionId)) {
      return <Navigate to={homeScreenRedirection()} replace />
    }
  }
  return children
}

const wrapRoutesWithProtectedRoute = (routes: any) => {
  // Map over the routes array
  return routes.map((route: any) => {
    // Check if the route has children
    if (route.children) {
      // If it has children, recursively wrap the children with protected route.
      // Also guard the parent's own element (e.g. <SidebarLayout/>) so an unauthenticated user is
      // redirected to /login BEFORE the layout mounts — otherwise the layout renders (and can crash)
      // before the child route's ProtectedRoute gets a chance to redirect.
      const children = wrapRoutesWithProtectedRoute(route.children)
      return {
        ...route,
        element: route?.private ? (
          <ProtectedRoute key={route.path} permission={route.permission}>
            {route.element}
          </ProtectedRoute>
        ) : (
          route.element
        ),
        children,
      }
    } else {
      // If the route does not have children, determine whether to wrap it with protected or public route
      return {
        ...route,
        element: route?.private ? (
          <ProtectedRoute key={route.path} permission={route.permission}>
            {route.element}
          </ProtectedRoute>
        ) : (
          <PublicRoute key={route.path}>{route.element}</PublicRoute>
        ),
      }
    }
  })
}

/**
 * Every tab strip inside project management, as data. Row 1 (`project-page`) is implicit; these
 * are the levels beneath it, applied recursively by hoistProjectTabs. Adding a section means
 * adding a line here — no route literal should render TabsLayout itself.
 *
 * `listOnly` keeps the capsule strips off detail pages: a single record showing
 * `Schedule · Report · Non-Conformance` reads as the wrong context.
 */
const TAB_LEVELS: TabLevel[] = [
  { segment: 'nai-flow', page: 'nai-flow', variant: 'tertiary' },
  { segment: 'nai-flow-view', page: 'nai-flow-view', variant: 'tertiary' },
  { segment: 'user-management', page: 'project-user-management', variant: 'tertiary' },
  { segment: 'inspections', page: 'inspections', variant: 'capsule', listOnly: true },
  { segment: 'audits', page: 'audits', variant: 'capsule', listOnly: true },
  { segment: 'statistics-capture', page: 'statistics-capture', variant: 'capsule', listOnly: true },
]

/** The dashboard's own levels, hoisted via the `hoistLevels` marker on its shared strip. */
const DASHBOARD_LEVELS: TabLevel[] = [
  { segment: 'nai-flow', page: 'nai-flow-dashboard', variant: 'tertiary' },
  { segment: 'nai-hub', page: 'nai-hub-dashboard', variant: 'tertiary' },
]

/**
 * Generates every tab strip: the project-management scope first, then any block that declares
 * `hoistStrip` / `hoistLevels`. Route literals therefore never render TabsLayout themselves —
 * a route that genuinely needs its own strip sets `customTabs: true`.
 */
const hoistAllTabs = (routeList: RouteLike[]) => applyTabLevels(hoistProjectTabs(routeList, TAB_LEVELS, makeTabParent), makeTabParent)

// Builds the shared parent route that owns a hoisted tab strip. The grouping logic lives in
// hoistProjectTabs.ts, kept React-free so it can be unit tested — this file pulls in the api
// layer, and axios ships ESM that Jest cannot parse.
const makeTabParent: TabParentFactory = ({ path, page, variant, parentPage, children }) => ({
  path,
  private: true,
  element: (
    <TabsLayout page={page} parentPage={parentPage} skipBreadcrumb variant={variant}>
      {/* Keep the existing loader design, but catch lazy leaves below the persistent strip.
          Otherwise AppLayout's boundary replaces this TabsLayout too and hides the tabs. */}
      <Suspense
        fallback={
          <div className="relative min-h-[calc(100vh-9rem)]">
            <ContentLoader />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </TabsLayout>
  ),
  children,
})

export const routes = [
  ...wrapRoutesWithProtectedRoute(
    hoistAllTabs([
      {
        path: '',
        private: false,
        element: <Login />,
      },
      {
        path: 'login',
        private: false,
        element: <Login />,
      },
      {
        path: 'ai-insights',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'AI Insights', redirect: '/ai-insights' }]}>
                <AIInsights />
              </PageBreadcrumb>
            ),
          },
        ],
      },
      {
        path: 'otp-verification',
        private: false,
        element: <OTPVerification />,
      },
      {
        path: 'select-workspace',
        private: false,
        element: <SelectWorkspace />,
      },
      // AI RAMS Assessment
      {
        path: 'ai-rams-assessment',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'AI RAMS Assessment', redirect: '/ai-rams-assessment' }]}>
                <AiRamsAssessment />
              </PageBreadcrumb>
            ),
          },
        ],
      },
      // New Dashboard flow
      {
        path: 'dashboard/',
        private: true,
        element: <AppLayout />,
        children: [
          {
            // Shared Overview/nAI Flow/nAI Hub tab strip for every dashboard route below.
            // skipBreadcrumb leaves breadcrumb dispatch to each child.
            path: '',
            private: true,
            // Explicit <Outlet/>: React Router would supply one for an element-less route, but
            // wrapRoutesWithProtectedRoute wraps every private parent as
            // <ProtectedRoute>{route.element}</ProtectedRoute> — with no element that renders
            // undefined and the whole subtree disappears. The strip itself comes from
            // hoistStrip below and mounts as this route's generated child.
            element: <Outlet />,
            hoistStrip: { page: 'organisation-dashboard', variant: 'secondary' },
            hoistLevels: DASHBOARD_LEVELS,
            children: [
              {
                path: 'overview',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/overview' },
                      { title: 'Overview', redirect: '/dashboard/overview' },
                    ]}
                  >
                    <OverviewDashboardNew />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'overview-old',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/observation' },
                      { title: 'Overview', redirect: '/dashboard/overview-old' },
                    ]}
                  >
                    <OverviewDashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/observation-old',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow/observation' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow/observation' },
                      { title: 'Observation', redirect: '/dashboard/nai-flow/observation' },
                    ]}
                  >
                    <Dashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow',
                private: true,
                element: <NaiFlowDashboardRedirect />,
              },
              {
                path: 'nai-flow/observation',
                private: true,
                element: <ObservationRouteGuard />,
              },
              {
                path: 'nai-flow/observations-listing',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Observations', redirect: '/dashboard/nai-flow/observations-listing' },
                    ]}
                  >
                    <ObservationsListing />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/incidents-listing',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow/incident' },
                      { title: 'Incidents', redirect: '/dashboard/nai-flow/incidents-listing' },
                    ]}
                  >
                    <IncidentsListing />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/inspections-listing',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow/inspection' },
                      { title: 'Inspections', redirect: '/dashboard/nai-flow/inspections-listing' },
                    ]}
                  >
                    <InspectionsListing />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/audits-listing',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow/audit' },
                      { title: 'Audits', redirect: '/dashboard/nai-flow/audits-listing' },
                    ]}
                  >
                    <InspectionsListing moduleType="audit" />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/trainings-listing',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow/training' },
                      { title: 'Trainings', redirect: '/dashboard/nai-flow/trainings-listing' },
                    ]}
                  >
                    <TrainingsListing />
                  </PageBreadcrumb>
                ),
              },
              {
                path: '',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard' },
                      { title: 'Observations', redirect: '/dashboard' },
                    ]}
                  >
                    <Dashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/statistics-capture',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Statistics Capture', redirect: '/dashboard/nai-flow/statistics-capture' },
                    ]}
                  >
                    <StatisticsCaptureDashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/incident',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Incident', redirect: '/dashboard/nai-flow/incident' },
                    ]}
                  >
                    <IncidentDashboardNew />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/incident-old',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Incident (Old)', redirect: '/dashboard/nai-flow/incident-old' },
                    ]}
                  >
                    <IncidentDashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/inspection',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Inspection', redirect: '/dashboard/nai-flow/inspection' },
                    ]}
                  >
                    <InspectionDashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/audit',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Audit', redirect: '/dashboard/nai-flow/audit' },
                    ]}
                  >
                    <InspectionDashboard moduleType="audit" />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/training',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Training', redirect: '/dashboard/nai-flow/training' },
                    ]}
                  >
                    <TrainingDashboardNew />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-flow/training-old',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
                      { title: 'Training (Old)', redirect: '/dashboard/nai-flow/training-old' },
                    ]}
                  >
                    <TrainingDashboard />
                  </PageBreadcrumb>
                ),
              },
              {
                path: 'nai-hub',
                private: true,
                element: <NaiHubDashboardRedirect />,
              },
              // NaiHub Dashboard
              {
                path: 'nai-hub/:name/:id',
                private: true,
                element: (
                  <PageBreadcrumb
                    breadcrumbData={[
                      { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
                      { title: 'nAI Hub', redirect: '/dashboard/nai-hub/observation' },
                    ]}
                  >
                    <NaiHubDashboard />
                  </PageBreadcrumb>
                ),
              },
            ],
          },
        ],
      },
      // Masters
      {
        path: 'masters',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'Masters', redirect: '/masters' }]}>
                <MastersHome />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'division',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Division', redirect: '/masters/division' },
                ]}
              >
                <Division />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'hazard-type',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Hazard Type', redirect: '/masters' },
                ]}
              >
                <HazardType />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'root-cause',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Root Cause', redirect: '/masters' },
                ]}
              >
                <RootCause />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'severity',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Severity', redirect: '/masters' },
                ]}
              >
                <Severity />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'observation-status',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Observation Status', redirect: '/masters' },
                ]}
              >
                <ObservationStatus />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'statistics-capture-status',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Statistics Capture Status', redirect: '/masters' },
                ]}
              >
                <StatisticsCaptureStatus />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'statistics-capture-inputs',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Statistics Capture Inputs', redirect: '/masters' },
                ]}
              >
                <StatisticsCaptureInputs />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'incident-status',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Incident Status', redirect: '/masters' },
                ]}
              >
                <IncidentStatus />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'incident-category',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Incident Category', redirect: '/masters' },
                ]}
              >
                <IncidentCategory />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'incident-severity',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Incident Severity', redirect: '/masters' },
                ]}
              >
                <IncidentSeverity />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'inspection-status',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Inspection Status', redirect: '/masters' },
                ]}
              >
                <InspectionStatus />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'inspection-schedule-status',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Inspection Schedule Status', redirect: '/masters' },
                ]}
              >
                <InspectionScheduleStatus />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'inspection-non-conformance-status',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Inspection Non Conformance Status', redirect: '/masters' },
                ]}
              >
                <InspectionNonConformanceStatus />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'inspection-sub-category',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Inspection Sub Category', redirect: '/masters' },
                ]}
              >
                <InspectionSubCategory />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'inspection-topic',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Inspection Topic', redirect: '/masters' },
                ]}
              >
                <InspectionTopic />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'inspection-questions',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Inspection Questions', redirect: '/masters' },
                ]}
              >
                <InspectionQuestions />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'training-category',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Masters', redirect: '/masters' },
                  { title: 'Training Category', redirect: '/masters' },
                ]}
              >
                <TrainingCategory />
              </PageBreadcrumb>
            ),
          },
        ],
      },

      // Project Management
      {
        path: 'project-management',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb>
                <ProjectManagement />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'add',
            private: true,
            element: (
              <PageBreadcrumb>
                <AddProject />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/details',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'Details', redirect: '/project-management/:name/:id/details' },
                ]}
              >
                <AddProject mode={'detail'} />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/workflow',
            private: true,
            element: (
              <ProjectLayout
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'Workflow', redirect: '/project-management/:name/:id/details' },
                ]}
              >
                <ProjectWorkflow />
              </ProjectLayout>
            ),
          },
          {
            path: ':name/:id/rules-and-escalation',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                ]}
              >
                <RulesAndEscalation />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/rules-and-escalation/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                  { title: 'Add', redirect: '/project-management/:name/:id/rules-and-escalation' },
                ]}
                page={'project-page'}
              >
                <AddRulesAndEscalation />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/rules-and-escalation/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                  { title: ':subName', redirect: '/project-management/:name/:id/rules-and-escalation' },
                ]}
                page={'project-page'}
              >
                <AddRulesAndEscalation />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/rules-and-escalation/duplicate/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
                  { title: ':subName', redirect: '/project-management/:name/:id/rules-and-escalation' },
                ]}
                page={'project-page'}
              >
                <AddRulesAndEscalation isDuplicate={true} />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/user-management/users',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                  { title: 'Users', redirect: '/project-management/:name/:id/details' },
                ]}
                page={'project-user-management'}
              >
                <ProjectUsers />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/user-management/users/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                  { title: 'Users', redirect: '/project-management/:name/:id/user-management/users' },
                  { title: 'Add', redirect: '/project-management/:name/:id/user-management/users' },
                ]}
                page={'project-user-management'}
              >
                <AddUserToProject />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/user-management/users/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                  { title: 'Users', redirect: '/project-management/:name/:id/user-management/users' },
                  { title: ':subName', redirect: '/project-management/:name/:id/user-management/users' },
                ]}
                page={'project-user-management'}
              >
                <AddUserToProject />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/user-management/roles',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                  { title: 'Roles', redirect: '/project-management/:name/:id/details' },
                ]}
                page={'project-user-management'}
              >
                <ProjectRoles />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/user-management/roles/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                  { title: 'Roles', redirect: '/project-management/:name/:id/user-management/roles' },
                  { title: 'Add', redirect: '/project-management/:name/:id/user-management/roles' },
                ]}
                page={'project-user-management'}
              >
                <AddRoleToProject />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/user-management/roles/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
                  { title: 'Roles', redirect: '/project-management/:name/:id/user-management/roles' },
                  { title: ':subName', redirect: '/project-management/:name/:id/user-management/roles' },
                ]}
                page={'project-user-management'}
              >
                <AddRoleToProject />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/whatsapp-group',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/nai-flow' },
                ]}
                page={'nai-flow'}
              >
                <WhatsappGroup />
              </PageBreadcrumb>
            ),
          },
          /*  {
 path: 'project-management/:name/:id/nai-flow-view/whatsapp-group',
 private: true,
 element: (
   <PageBreadcrumb
     breadcrumbData={[
       { title: 'Project Management', redirect: '/project-management' },
       { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
       { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
       { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/nai-flow' },
     ]}
     page={'nai-flow-view'}
   >
     <WhatsappGroupView />
   </PageBreadcrumb>
 ),
     }, */
          {
            path: ':name/:id/nai-flow/observations',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow/observations' },
                ]}
                page={'nai-flow'}
              >
                <Observations />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/observations',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Observations', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/observations' },
                ]}
                page={'nai-flow-view'}
              >
                <ObservationsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/observations/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Observations', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/observations' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/observations' },
                ]}
                page={'nai-flow-view'}
                disableBreadcrumbLinks={true}
              >
                <ObservationDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Incidents
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/incidents',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Incidents', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
                ]}
                page={'nai-flow-view'}
              >
                <IncidentsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/incidents/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Incidents', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
                ]}
                page={'nai-flow-view'}
                disableBreadcrumbLinks={true}
              >
                <IncidentDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspections
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <ScheduleView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/list',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Inspections List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <InspectionsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                ]}
                page={'nai-flow-view'}
              >
                <NonConformanceView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Trainings
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/trainings',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Trainings', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
                ]}
                page={'nai-flow-view'}
              >
                <TrainingsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Training Details
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/trainings/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Trainings', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
                ]}
                page={'nai-flow-view'}
                disableBreadcrumbLinks={true}
              >
                <TrainingDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Site Walkthrough
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/site-walkthrough',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough' },
                ]}
                page={'nai-flow-view'}
              >
                <SiteWalkthroughView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/site-walkthrough/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough?site_walkthrough_id=:subName' },
                  { title: ':subId', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/site-walkthrough' },
                ]}
                page={'nai-flow-view'}
              >
                <SiteWalkthroughDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspection Details
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/list/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Inspections List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list' },
                ]}
                page={'nai-flow-view'}
              >
                <InspectionDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Statistics Capture
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <StatisticsCaptureView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Reports', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports' },
                ]}
                page={'nai-flow-view'}
              >
                <StatisticsCaptureReportView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Reports', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports' },
                ]}
                page={'nai-flow-view'}
              >
                <EditReportView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Add Inspection Schedule
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspection Schedule Edit
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspection Non-Conformance Details
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                ]}
              >
                <NonConformanceDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Audits (form-based, reuses the inspection view components via moduleType)
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <ScheduleView moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/list',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Audits List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/list' },
                ]}
                page={'nai-flow-view'}
              >
                <InspectionsView moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/list/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Audits List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/list' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/list' },
                ]}
                page={'nai-flow-view'}
              >
                <InspectionDetailsView moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspection Schedule Edit
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspection Non-Conformance Details
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                ]}
              >
                <NonConformanceDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Audits (form-based, reuses the inspection view components via moduleType)
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Inspection Non-Conformance Details
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
                ]}
              >
                <NonConformanceDetailsView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Audits (form-based, reuses the inspection view components via moduleType)
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddScheduleView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Statistics Capture Schedule Details
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <StatisticsCaptureView />
              </PageBreadcrumb>
            ),
          },
          // Linked Projects - Whatsapp Group
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/whatsapp-group',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/whatsapp-group' },
                ]}
                page={'nai-flow-view'}
              >
                <WhatsappGroupView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/observations/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow/Observations' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/observations' },
                ]}
                page={'nai-flow'}
              >
                <ObservationDetails />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/observations/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/Observations' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
                ]}
                page={'nai-flow-view'}
              >
                <ObservationDetailsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/incidents',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow/incidents' },
                ]}
                page={'nai-flow'}
              >
                <Incidents />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/site-walkthrough',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough' },
                ]}
                page={'nai-flow'}
              >
                <SiteWalkthrough />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/site-walkthrough/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Site Walkthrough', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough?site_walkthrough_id=:subName' },
                  { title: ':subId', redirect: '/project-management/:name/:id/nai-flow/site-walkthrough' },
                ]}
                page={'nai-flow'}
              >
                <SiteWalkthroughDetails />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/incidents',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
                ]}
                page={'nai-flow-view'}
              >
                <IncidentsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/incidents/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow/incidents' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/incidents' },
                ]}
                page={'nai-flow'}
              >
                <IncidentDetails />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/incidents/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
                ]}
                page={'nai-flow-view'}
              >
                <IncidentDetailsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/list',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                ]}
                page={'inspections'}
                parentPage={'nai-flow'}
              >
                <Inspections />
              </PageBreadcrumb>
            ),
          },
          // --- Audit module (reuses the inspection List + Schedule pages with moduleType='audit') ---
          {
            path: ':name/:id/nai-flow/audits/list',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                  { title: 'Audits List', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                ]}
                page={'audits'}
                parentPage={'nai-flow'}
              >
                <Inspections moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/audits/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                ]}
                page={'audits'}
                parentPage={'nai-flow'}
              >
                <Schedule moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/audits/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                ]}
                page={'nai-flow'}
              >
                <AddInspectionSchedule moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/audits/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                ]}
                page={'nai-flow'}
              >
                <AddInspectionSchedule moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/audits/list/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                  { title: 'Audits List', redirect: '/project-management/:name/:id/nai-flow/audits/list' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/audits/list' },
                ]}
                page={'nai-flow'}
              >
                <InspectionDetails moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/list',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                ]}
                page={'inspections'}
                parentPage={'nai-flow-view'}
              >
                <InspectionsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/list/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow/Inspections/list' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/list' },
                ]}
                page={'nai-flow'}
              >
                <InspectionDetails />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/list/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/list' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/list' },
                ]}
                page={'nai-flow-view'}
              >
                <InspectionDetailsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                ]}
                page={'inspections'}
                parentPage={'nai-flow'}
              >
                <Schedule />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                ]}
                page={'inspections'}
                parentPage={'nai-flow-view'}
              >
                <ScheduleView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                ]}
              >
                <AddInspectionSchedule />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                ]}
              >
                <AddInspectionScheduleView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                ]}
                page={'nai-flow'}
              >
                <AddInspectionSchedule />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddInspectionScheduleView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
                ]}
                page={'inspections'}
                parentPage={'nai-flow'}
              >
                <NonConformance />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
                ]}
                page={'inspections'}
                parentPage={'nai-flow-view'}
              >
                <NonConformanceView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/inspections/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
                ]}
              >
                <NonConformanceDetails />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/inspections/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
                ]}
              >
                <NonConformanceDetailsView />
              </PageBreadcrumb>
            ),
          },
          // --- Audit NCR routes (Phase 5) ---
          {
            path: ':name/:id/nai-flow/audits/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/audits/non-conformance' },
                ]}
                page={'audits'}
                parentPage={'nai-flow'}
              >
                <NonConformance moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/audits/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/nai-flow/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/audits/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/audits/non-conformance' },
                ]}
              >
                <NonConformanceDetails moduleType="audit" />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                ]}
                page={'audits'}
                parentPage={'nai-flow-view'}
              >
                <NonConformance moduleType="audit" isView={true} />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                ]}
              >
                <NonConformanceDetails moduleType="audit" isView={true} />
              </PageBreadcrumb>
            ),
          },
          // --- Audit NCR routes (Phase 5) ---
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                ]}
                page={'audits'}
                parentPage={'nai-flow-view'}
              >
                <NonConformance moduleType="audit" isView={true} />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                ]}
              >
                <NonConformanceDetails moduleType="audit" isView={true} />
              </PageBreadcrumb>
            ),
          },
          // --- Audit NCR routes (Phase 5) ---
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                ]}
                page={'audits'}
                parentPage={'nai-flow-view'}
              >
                <NonConformance moduleType="audit" isView={true} />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
                  { title: 'Audits', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/schedule' },
                  { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                  { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/audits/non-conformance' },
                ]}
              >
                <NonConformanceDetails moduleType="audit" isView={true} />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/trainings',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow/trainings' },
                ]}
                page={'nai-flow'}
              >
                <Trainings />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/trainings',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
                ]}
                page={'nai-flow-view'}
              >
                <TrainingsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/trainings/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow/trainings' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/trainings' },
                ]}
                page={'nai-flow'}
              >
                <TrainingDetails />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/trainings/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
                ]}
                page={'nai-flow-view'}
              >
                <TrainingDetailsView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/statistics-capture/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                ]}
                page={'statistics-capture'}
                parentPage={'nai-flow'}
              >
                <StatisticsCapture />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/statistics-capture/schedule',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                ]}
                page={'statistics-capture'}
                parentPage={'nai-flow-view'}
              >
                <StatisticsCaptureView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/statistics-capture/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                ]}
                page={'nai-flow'}
              >
                <AddSchedule />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/statistics-capture/schedule/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddScheduleView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/statistics-capture/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                ]}
                page={'nai-flow'}
              >
                <AddSchedule />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/statistics-capture/schedule/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                ]}
                page={'nai-flow-view'}
              >
                <AddScheduleView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/statistics-capture/reports',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                  { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
                ]}
                page={'statistics-capture'}
                parentPage={'nai-flow'}
              >
                <StatisticsCaptureReport />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/statistics-capture/reports',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
                ]}
                page={'statistics-capture'}
                parentPage={'nai-flow-view'}
              >
                <StatisticsCaptureReportView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow/statistics-capture/reports/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
                  { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
                ]}
                page={'nai-flow'}
              >
                <EditReport />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-flow-view/statistics-capture/reports/:subName/:subId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
                  { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
                  { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
                ]}
                page={'nai-flow-view'}
              >
                <EditReportView />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-hub',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                ]}
              >
                <NaiHub />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-hub/add',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                  { title: 'Add nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                ]}
              >
                <AddNaiHub />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':name/:id/nai-hub/edit/:subName/:subId/:resourceName/:resourceId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Project Management', redirect: '/project-management' },
                  { title: ':name', redirect: '/project-management/:name/:id/details' },
                  { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
                  { title: ':subName', redirect: '/project-management/:name/:id/nai-hub' },
                ]}
              >
                <Outlet />
              </PageBreadcrumb>
            ),
            children: [
              { path: 'details', private: true, element: <AddNaiHub mode="detail" /> },
              { path: 'nai-hub-dashboard', private: true, element: <NaiHubDashboard /> },
            ],
          },
        ],
      },
      // nAI Connect
      {
        path: 'nai-connect',
        private: true,
        permission: { slug: 'nai-connect', permissionId: 1 },
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'nAI Connect', redirect: '/nai-connect' }]}>
                <NaiConnect />
              </PageBreadcrumb>
            ),
          },
        ],
      },
      // Quiz Assessment
      {
        path: 'quiz-assessment',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'Quiz Assessment', redirect: '/quiz-assessment' }]}>
                <QuizAssessment />
              </PageBreadcrumb>
            ),
          },
          {
            path: ':id',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Quiz Assessment', redirect: '/quiz-assessment' },
                  { title: 'Details', redirect: '/quiz-assessment/:id' },
                ]}
              >
                <QuizAssessmentDetails />
              </PageBreadcrumb>
            ),
          },
        ],
      },
      {
        path: 'reports',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb>
                <div className="h-full p-8">
                  <ComingSoon pageName="Statistics Capture" />
                </div>
              </PageBreadcrumb>
            ),
          },
        ],
      },

      // User Management
      {
        path: 'user-management',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: 'user-role',
            private: true,
            element: (
              <UserManagementLayout
                defaultIndex={1}
                breadcrumbData={[
                  { title: 'User Management', redirect: '/user-management/user-role' },
                  { title: 'User Role', redirect: '/user-management/user-role' },
                ]}
              >
                <Outlet />
              </UserManagementLayout>
            ),
            children: [
              { path: '', private: true, element: <UserRole /> },
              { path: ':id', private: true, element: <AddUserRole /> },
            ],
          },
          {
            path: 'users',
            private: true,
            element: (
              <UserManagementLayout
                defaultIndex={0}
                breadcrumbData={[
                  { title: 'User Management', redirect: '/user-management/users' },
                  { title: 'Users', redirect: '/user-management/users' },
                ]}
              >
                <Outlet />
              </UserManagementLayout>
            ),
            children: [
              { path: '', private: true, element: <UserListing /> },
              { path: ':id', private: true, element: <AddUser /> },
            ],
          },
        ],
      },
      // Video Script Generation (permission slug: video-script-generation)
      {
        path: 'video-script-generation',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'Video Script Generation', redirect: '/video-script-generation' }]}>
                <VideoScriptGeneration />
              </PageBreadcrumb>
            ),
          },
        ],
      },
      // Workflow Builder
      {
        path: 'workflow-builder',
        private: true,
        element: <AppLayout />,
        children: [
          {
            path: '',
            private: true,
            element: (
              <PageBreadcrumb breadcrumbData={[{ title: 'Workflow Builder', redirect: '/workflow-builder' }]}>
                <WorkflowBuilder />
              </PageBreadcrumb>
            ),
          },
          {
            path: 'edit/:formId',
            private: true,
            element: (
              <PageBreadcrumb
                breadcrumbData={[
                  { title: 'Workflow Builder', redirect: '/workflow-builder' },
                  { title: 'Edit', redirect: '' },
                ]}
              >
                <EditWorkflow />
              </PageBreadcrumb>
            ),
          },
        ],
      },
    ]),
  ),
  {
    path: 'check-app',
    element: (
      <LRFLayout>
        <CheckAppInstalled />
      </LRFLayout>
    ),
  },
  {
    path: 'sso',
    private: false,
    element: <SSO />,
  },
  {
    path: '*',
    element: <PageNotFound />,
  },
]

// Single source of truth for the subsidiary-ID-in-URL boundary (SubsidiaryBoundary) —
// derived from the routes above so the two never drift apart.
export const privateTopLevelSegments = routes.filter((r) => r.private).map((r) => r.path.replace(/\/$/, ''))
export const publicTopLevelSegments = routes.filter((r) => !r.private && r.path !== '*').map((r) => r.path.replace(/\/$/, ''))
